/* GASSISA
========================================================================================
* Descripción: Formulario para buscar y reimprimir tickets de caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Vista previa ESC/POS para tickets de caja
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { Fragment, useState, useEffect } from "react";
import { Paper, Typography, Box, Stack, Divider, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tabs, Tab, Select, MenuItem, FormControl, InputLabel, Modal, Tooltip } from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

import SizeSoftXLayout from "../leyout/SizeSoftXLayout";
import MnuReimpresionCajaActions from "./MnuReimpresionCajaActions.jsx";
import DatePicker from "react-datepicker";
import { useVentas } from "../../context/Ventas/VentasContext";
import "react-datepicker/dist/react-datepicker.css";

import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import {
    apiObtenerListaCotizaciones,
    apiObtenerRemisionVenta,
    apiObtenerCotizacionImpresion,
    apiListadoVentasCaja
} from "../../settings/ApiConfig";
import { concatenarPartes } from "../../settings/ticketEscPos";
import { buildTicketVentaParts, layoutTieneObjetos, normalizarDataTicketCotizacion } from "../../settings/ticketVentaEscape";
import { buildTicketCotizacionClasicoParts, buildTicketVentaClasicoParts } from "../../settings/ticketVentaClasico";
import { imprimirTicketEnPrinterTools, parseEscPosPreview } from "../../settings/ticketImpresion";
import { metricasPreviewTicket } from "../../settings/ticketPerfil";

const groupByRemision = (rows) => {
    const seen = new Set();
    return (rows || []).filter(row => {
        if (seen.has(row.remision)) return false;
        seen.add(row.remision);
        return true;
    });
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(value || 0);
};

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString('es-MX');
};

const formatDateTimeQuery = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

const ticketPreviewModalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(560px, 94vw)",
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1,
};


export default function FrmReimpresionCaja() {
    const { isLocked: isFormLocked } = useFormTabLock('reimpresion_caja', {
        title: 'Reimpresión Caja',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Reimpresión Caja'} ya está abierto en otra pestaña`);
        }
    });

    const { parametrosCajaForm, usuarioData } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { GetRequest } = useRequest();
    const { listaCajas, handleOntenerListaCajasRegistro, handleClickRegresarMainDashboard, handleImprimirTicket } = useVentas();

    const [tab, setTab] = useState(0);
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
    const [fechaFin, setFechaFin] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [cajaSeleccionada, setCajaSeleccionada] = useState("");
    const [openVistaPrevia, setOpenVistaPrevia] = useState(false);
    const [vistaPreviaTitulo, setVistaPreviaTitulo] = useState("");
    const [vistaPreviaLineas, setVistaPreviaLineas] = useState([]);
    const metricasVistaPrevia = metricasPreviewTicket(
        parametrosCajaForm?.ticketLayout?.columnas ?? 32,
        parametrosCajaForm?.ticketLayout?.calibracion
    );
    const altoLineaGrandePreview = Math.max(
        metricasVistaPrevia.lineHeight,
        metricasVistaPrevia.fontSize * 2 * 1.2
    );

    useEffect(() => {
        if (isFormLocked) {
            alert("El formulario de Reimpresión Caja ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
            setTimeout(() => {
                if (handleClickRegresarMainDashboard) handleClickRegresarMainDashboard();
            }, 900);
        }
    }, [isFormLocked, handleClickRegresarMainDashboard]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key.trim().toUpperCase() === "F4") {
                event.preventDefault();
                event.stopPropagation();
                handleClickRegresarMainDashboard();
                return;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleClickRegresarMainDashboard]);

    useEffect(() => {
        handleOntenerListaCajasRegistro();
    }, []);

    useEffect(() => {
        if (listaCajas && listaCajas.length > 0) {
            const cajasTrimmed = listaCajas.map(c => c.trim());
            if (parametrosCajaForm?.caja && parametrosCajaForm.caja !== 'CAJA-DEFAULT') {
                const match = cajasTrimmed.indexOf(parametrosCajaForm.caja.trim());
                setCajaSeleccionada(match >= 0 ? cajasTrimmed[match] : cajasTrimmed[0]);
            } else if (!cajaSeleccionada) {
                setCajaSeleccionada(cajasTrimmed[0]);
            } else {
                const currentTrimmed = cajaSeleccionada.trim();
                if (!cajasTrimmed.includes(currentTrimmed)) {
                    setCajaSeleccionada(cajasTrimmed[0]);
                }
            }
        }
    }, [parametrosCajaForm, listaCajas]);

    const getCajaActual = () => {
        return cajaSeleccionada || parametrosCajaForm?.caja || "";
    };

    const handleCajaChange = (event) => {
        setCajaSeleccionada(event.target.value);
        setCotizaciones([]);
        setVentas([]);
    };

    const handleBuscarDatos = async () => {
        const caja = getCajaActual().trim();
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja para buscar", "warning");
            return;
        }

        setLoading(true);

        try {
            const params = new URLSearchParams();
            switch (tab) {
                case 0:
                    if (fechaInicio) params.append('fechainicial', formatDateTimeQuery(fechaInicio));
                    if (fechaFin) params.append('fechafinal', formatDateTimeQuery(fechaFin));
                    const urlVentas = `${apiListadoVentasCaja.replace('{pc}', caja.toUpperCase())}?${params.toString()}`;
                    const respVentas = await GetRequest({ url: urlVentas });
                    if (respVentas.hasError || !respVentas.result) {
                        setVentas([]);
                        handleOpenAlert("No hay ventas registradas para este rango de fechas en la caja seleccionada", "info");
                    } else {
                        setVentas(groupByRemision(respVentas.result));
                    }
                    break;
                case 1:
                    if (fechaInicio) params.append('fechaInicio', fechaInicio.toISOString());
                    if (fechaFin) params.append('fechaFin', fechaFin.toISOString());
                    const urlCotizaciones = `${apiObtenerListaCotizaciones.replace('{caja}', caja)}?${params.toString()}`;
                    const respCotizaciones = await GetRequest({ url: urlCotizaciones });
                    if (!respCotizaciones.hasError) {
                        setCotizaciones(respCotizaciones.result || []);
                    }
                    break;
            }
        } catch (error) {
            handleOpenAlert("Error al buscar datos: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const [aperturas, setAperturas] = useState([]);
    const [cierres, setCierres] = useState([]);
    const [retiros, setRetiros] = useState([]);
    const [cotizaciones, setCotizaciones] = useState([]);
    const [ventas, setVentas] = useState([]);

    const handleChangeTab = (event, newValue) => {
        setTab(newValue);
    };

    const abrirVistaPreviaTicket = (titulo, ticketParts) => {
        const ticketBytes = concatenarPartes(ticketParts);
        setVistaPreviaTitulo(titulo);
        setVistaPreviaLineas(parseEscPosPreview(ticketBytes, {
            columnasMax: metricasVistaPrevia.columnas,
            anchoCaracterDots: metricasVistaPrevia.anchoCaracterDots,
        }));
        setOpenVistaPrevia(true);
    };

    // El preview usa el MISMO motor que la impresion real (ticketVentaEscape):
    // si el diseno por objetos esta configurado se usa ese, si no el metodo legacy.
    // Asi lo que se ve en la vista previa es exactamente lo que sale en papel.
    const obtenerPartesTicketVenta = async (remision, caja) => {
        if (!remision || !caja) return null;

        const url = apiObtenerRemisionVenta
            .replace("{remision}", remision)
            .replace("{caja}", caja);

        const response = await GetRequest({
            url,
            loader: "Obteniendo remisión de venta en caja",
        });

        if (response.hasError) {
            handleOpenAlert(response.message, "error");
            return null;
        }

        if (!response.result) return null;

        const { partes } = layoutTieneObjetos(parametrosCajaForm)
            ? buildTicketVentaParts({
                dataTicket: response.result,
                parametrosCajaForm,
                esReimpresion: true,
            })
            : buildTicketVentaClasicoParts({
                dataTicket: response.result,
                parametrosCajaForm,
                esReimpresion: true,
            });
        return partes;
    };

    const buildTicketCotizacionParts = async (folioCotizacion, opciones = {}) => {
        const caja = getCajaActual();
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja", "warning");
            return null;
        }

        const url = apiObtenerCotizacionImpresion
            .replace("{folioCotizacion}", folioCotizacion)
            .replace("{pc}", caja);

        const response = await GetRequest({ url });

        if (response.hasError) {
            handleOpenAlert(response.message, "error");
            return null;
        }

        const dataTicketNormalizado = normalizarDataTicketCotizacion({
            dataTicket: response.result,
            folioCotizacion,
            caja,
            parametrosCajaForm,
        });

        const { partes } = layoutTieneObjetos(parametrosCajaForm)
            ? buildTicketVentaParts({
                dataTicket: dataTicketNormalizado,
                parametrosCajaForm,
                tipoDocumento: "COTIZACION",
                etiquetaFolio: "Cotizacion",
                tituloDocumento: "COTIZACION",
                esReimpresion: Boolean(opciones.esReimpresion),
            })
            : buildTicketCotizacionClasicoParts({
                dataTicket: response.result,
                folioCotizacion,
                caja,
            });

        return partes;
    };

    const handleVistaPreviaVenta = async (row) => {
        const ticketParts = await obtenerPartesTicketVenta(row.remision, getCajaActual());
        if (ticketParts) abrirVistaPreviaTicket(`Remisión ${row.remision}`, ticketParts);
    };

    const handleVistaPreviaCotizacion = async (row) => {
        const ticketParts = await buildTicketCotizacionParts(row.remision);
        if (ticketParts) abrirVistaPreviaTicket(`Cotización ${row.remision}`, ticketParts);
    };

    useEffect(() => {
        setCotizaciones([]);
        setVentas([]);
    }, [tab, cajaSeleccionada]);

    const buscarDatos = async () => {
        const caja = getCajaActual();
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja para buscar", "warning");
            return;
        }

        setLoading(true);

        try {
            switch (tab) {
                case 0: {
                    const params = new URLSearchParams();
                    if (fechaInicio) params.append('fechainicial', formatDateTimeQuery(fechaInicio));
                    if (fechaFin) params.append('fechafinal', formatDateTimeQuery(fechaFin));
                    const urlVentas = `${apiListadoVentasCaja.replace('{pc}', caja)}?${params.toString()}`;
                    const respVentas = await GetRequest({ url: urlVentas });
                    if (!respVentas.hasError && respVentas.result) {
                        setVentas(groupByRemision(respVentas.result));
                    }
                    break;
                }
                case 1: {
                    const params = new URLSearchParams();
                    if (fechaInicio) params.append('fechaInicio', fechaInicio.toISOString());
                    if (fechaFin) params.append('fechaFin', fechaFin.toISOString());
                    const urlCotizaciones = `${apiObtenerListaCotizaciones.replace('{caja}', caja)}?${params.toString()}`;
                    const respCotizaciones = await GetRequest({ url: urlCotizaciones });
                    if (!respCotizaciones.hasError) {
                        setCotizaciones(respCotizaciones.result || []);
                    }
                    break;
                }
            }
        } catch (error) {
            handleOpenAlert("Error al buscar datos: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const imprimirTicketApertura = async (data) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'APERTURA DE CAJA\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (data.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (data.usuario || "") + '\n'),
            textEncoder.encode('Fecha: ' + formatDate(data.fechaapertura) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        const monedas = data.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push('-'.repeat(32) + '\n');
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL: ' + formatCurrency(data.montoinicial) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (data.observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION APERTURA\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoBase64 = parametrosCajaForm?.logo;
        if (logoBase64) {
            try {
                const binary = atob(logoBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketCierre = async (data) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'CIERRE DE CAJA\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (data.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (data.usuario || "") + '\n'),
            textEncoder.encode('Fecha: ' + formatDate(data.fechacierre) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        ticketParts.push(
            textEncoder.encode('RESUMEN DEL DIA:\n'),
            textEncoder.encode('-'.repeat(32) + '\n'),
            textEncoder.encode('Efectivo recibido: ' + formatCurrency(data.totalefectivo) + '\n'),
            textEncoder.encode('Efectivo credito clientes: ' + formatCurrency(data.totalefectivocredito) + '\n'),
            textEncoder.encode('Total abonos: ' + formatCurrency(data.totalabonosapartados) + '\n'),
            textEncoder.encode('Total retiros: ' + formatCurrency(data.totalretiros) + '\n'),
            textEncoder.encode('Monto en caja: ' + formatCurrency(data.montocajaactual) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        const monedas = data.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('CONTEO DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push('-'.repeat(32) + '\n');
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL CIERRE: ' + formatCurrency(data.montocierre) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (data.diferencia !== 0) {
            const tipoDif = data.diferencia > 0 ? 'SOBRANTE' : 'FALTANTE';
            ticketParts.push(textEncoder.encode('Diferencia (' + tipoDif + '): ' + formatCurrency(Math.abs(data.diferencia)) + '\n'));
        }

        if (data.observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION CIERRE\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoBase64 = parametrosCajaForm?.logo;
        if (logoBase64) {
            try {
                const binary = atob(logoBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketRetiro = async (data) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'RETIRO DE CAPITAL\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (data.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (data.usuarioautorizado || "") + '\n'),
            textEncoder.encode('Fecha: ' + formatDate(data.fecharetiro) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        const monedas = data.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push('-'.repeat(32) + '\n');
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL RETIRO: ' + formatCurrency(data.montoretiro) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (data.observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION RETIRO\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoBase64 = parametrosCajaForm?.logo;
        if (logoBase64) {
            try {
                const binary = atob(logoBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketCotizacion = async (folioCotizacion) => {
        try {
            const partes = await buildTicketCotizacionParts(folioCotizacion, { esReimpresion: true });
            if (!partes) return;

            await imprimirTicketEnPrinterTools({
                partes,
                parametrosCajaForm,
                nombreImpresora: parametrosCajaForm?.nombreimpresoracotizaciones?.trim() || null,
            });

            handleOpenAlert("Cotización reimpresa", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir cotización: " + error.message, "error");
        }
    };



    useEffect(() => {
        buscarDatos();
    }, [tab]);

    return (
        <Fragment>
            <SizeSoftXLayout
                titlePage="Reimpresión de Tickets de Caja"
                buttonActions={
                    <MnuReimpresionCajaActions 
                        handleClickRegresarMainDashboard={handleClickRegresarMainDashboard}
                    />
                }
            >
                <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Caja</InputLabel>
                            <Select
                                value={cajaSeleccionada}
                                label="Caja"
                                onChange={handleCajaChange}
                                renderValue={(v) => v?.trim?.() || v}
                            >
                                {(listaCajas || []).map((caja) => {
                                    const trimmed = caja.trim();
                                    return (
                                        <MenuItem key={trimmed} value={trimmed}>
                                            {trimmed}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon fontSize="small" />
                            <DatePicker
                                selected={fechaInicio}
                                onChange={(date) => setFechaInicio(date)}
                                dateFormat="dd/MM/yyyy"
                                className="custom-datepicker"
                            />
                        </Box>
                        <Typography>hasta</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DatePicker
                                selected={fechaFin}
                                onChange={(date) => setFechaFin(date)}
                                dateFormat="dd/MM/yyyy"
                                className="custom-datepicker"
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleBuscarDatos}
                            disabled={loading}
                        >
                            Buscar
                        </Button>
                    </Stack>
                </Paper>

                <Paper elevation={2} sx={{ p: 2 }}>
                    <Tabs value={tab} onChange={handleChangeTab} sx={{ mb: 2 }}>
                        <Tab label="Ventas" />
                        <Tab label="Cotizaciones" />
                    </Tabs>

                    {loading && (
                        <Typography align="center" sx={{ p: 2 }}>Buscando...</Typography>
                    )}

                    {!loading && tab === 0 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Folio</TableCell>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Vendedor</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ventas.map((row, index) => (
                                        <TableRow key={`${row.remision}-${index}`}>
                                            <TableCell>{row.remision}</TableCell>
                                            <TableCell>{formatDate(row.fechaventa)}</TableCell>
                                            <TableCell>{row.razonsocial || "PUBLICO GENERAL"}</TableCell>
                                            <TableCell>{row.usuario}</TableCell>
                                            <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title="Vista previa">
                                                        <IconButton size="small" onClick={() => handleVistaPreviaVenta(row)}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Imprimir">
                                                        <IconButton size="small" onClick={() => handleImprimirTicket(row.remision, getCajaActual())}>
                                                            <PrintIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {ventas.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">No hay ventas en el periodo</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!loading && tab === 1 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Folio</TableCell>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>PC</TableCell>
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cotizaciones.map((row, index) => (
                                        <TableRow key={`${row.remision}-${index}`}>
                                            <TableCell>{row.remision}</TableCell>
                                            <TableCell>{formatDate(row.fecharegistro)}</TableCell>
                                            <TableCell>{row.razonsocial}</TableCell>
                                            <TableCell>{row.pc}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title="Vista previa">
                                                        <IconButton size="small" onClick={() => handleVistaPreviaCotizacion(row)}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Imprimir">
                                                        <IconButton size="small" onClick={() => imprimirTicketCotizacion(row.remision)}>
                                                            <PrintIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {cotizaciones.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No hay cotizaciones en el periodo</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
                <Modal open={openVistaPrevia} onClose={() => setOpenVistaPrevia(false)}>
                    <Box sx={ticketPreviewModalStyle}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Vista previa ticket - {vistaPreviaTitulo}
                            </Typography>
                            <IconButton onClick={() => setOpenVistaPrevia(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        <Divider />
                        <Box
                            sx={{
                                overflow: "auto",
                                maxHeight: "72vh",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                bgcolor: "#f3f4f6",
                                p: 2,
                                borderRadius: "6px",
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${metricasVistaPrevia.anchoPx}px`,
                                    minHeight: "480px",
                                    height: "auto",
                                    bgcolor: "#fff",
                                    color: "#111",
                                    boxShadow: "0 8px 24px rgba(0,0,0,.16)",
                                    border: "1px solid #ddd",
                                    p: "14px 12px",
                                    fontFamily: "'Courier New', monospace",
                                    fontSize: `${metricasVistaPrevia.fontSize}px`,
                                    lineHeight: `${metricasVistaPrevia.lineHeight}px`,
                                    whiteSpace: "pre",
                                }}
                            >
                                {parametrosCajaForm?.logo ? (
                                    <Box sx={{ textAlign: "center", mb: 1 }}>
                                        <img
                                            src={`data:image/png;base64,${parametrosCajaForm.logo}`}
                                            alt="Logo"
                                            style={{ maxWidth: "120px", height: "auto" }}
                                        />
                                    </Box>
                                ) : null}
                                {vistaPreviaLineas.map((line, index) => (
                                    <Box
                                        key={`${index}-${line.text}`}
                                        sx={{
                                            textAlign: line.align,
                                            fontWeight: line.bold ? 800 : 400,
                                            fontSize: line.large
                                                ? `${metricasVistaPrevia.fontSize * 2}px`
                                                : `${metricasVistaPrevia.fontSize}px`,
                                            lineHeight: line.large
                                                ? `${altoLineaGrandePreview}px`
                                                : `${metricasVistaPrevia.lineHeight}px`,
                                            minHeight: line.large
                                                ? `${altoLineaGrandePreview}px`
                                                : `${metricasVistaPrevia.lineHeight}px`,
                                            whiteSpace: "pre",
                                        }}
                                    >
                                        {line.text || " "}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </SizeSoftXLayout>
        </Fragment>
    );
}
